import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BiotechRoundedIcon from '@mui/icons-material/BiotechRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Box, Grid, IconButton, Tab, Tabs, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { QueryParamConfig, StringParam, withDefault } from 'use-query-params';

import CellRulesIcon from 'components/atoms/CellRulesIcon';
import { TableEditingContextProvider } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { RowSelectionContextProvider } from 'components/atoms/RowSelectionContext';
import { useCellColorMappingFieldsContext } from 'interfaces/cellColorMapping/cellColorMappingFields';
import { Permission } from 'interfaces/permissionOption';
import { useNavigateBackFromAdmin } from 'utils/adminNavigation';
import useTaxonomy from 'utils/queryHooks/taxonomy/useTaxonomy';
import { useQueryableTypes } from 'utils/queryHooks/uiConstantsHooks';
import { useQueryParamAsLocalState } from 'utils/statelessUpdateUrlParams';
import { usePermissions } from 'utils/usePermissions';
import { wrapAdminPage } from '../AdminPageLayout';
import { AreaTypesDataGrid } from './AreaTypesDataGrid';
import { getAreaTypeId } from './AreaTypesDataGrid/utils';
import { CellColorMappingsDataGrid } from './CellColorMappingsDataGrid';
import { GlobalCellRulesPanel } from './CellRulesDataGrid/GlobalCellRules';
import { ClusterTypesDataGrid } from './ClusterTypesDataGrid';
import { ExternalLabelsDataGrid } from './ExternalLabelsDataGrid';
import { ModelTypesDataGrid } from './ModelTypesDataGrid';
import { getModelTypeId } from './ModelTypesDataGrid/utils';
import { PresetsDataGrid } from './PresetsDataGrid';
import { SlideTagsDataGrid } from './SlideTagsDataGrid';
import { StainTypesDataGrid } from './StainTypesDataGrid';
import { getStainTypeId } from './StainTypesDataGrid/utils';
import TaxonomyEditor from './Taxonomy';
import { TaxonomyTagsDataGrid } from './TaxonomyTagsDataGrid';

export const OperationsSettings = wrapAdminPage(() => {
  const { hasPermission } = usePermissions();
  const navigateBackFromAdmin = useNavigateBackFromAdmin();

  const canEditStainTypes = hasPermission(Permission.EditStainTypeOptions);
  const canEditSlideTags = hasPermission(Permission.EditSlideTagOptions);
  const canEditExternalLabels = hasPermission(Permission.EditExternalLabelOptions);
  const canEditAreaTypes = hasPermission(Permission.EditAreaTypeOptions);
  const canEditClusterTypes = hasPermission(Permission.EditClusterTypeOptions);
  const canEditTaxonomy = hasPermission(Permission.EditTaxonomyOptions);
  const canEditCellRules = hasPermission(Permission.EditCellRules);
  const canEditModelTypes = hasPermission(Permission.EditModelTypeOptions);
  const canEditCellColorMappings = hasPermission(Permission.EditCellColorMappings);
  const canPublishPresets = hasPermission(Permission.PublishCalculateFeaturesPreset);
  const canMakePresetsGlobal = hasPermission(Permission.MakeCalculateFeaturesPresetGlobal);
  const canViewAllPresetDrafts = hasPermission(Permission.ViewAllCalculateFeaturesPresetDrafts);
  const canViewPresetManagement = canPublishPresets || canMakePresetsGlobal || canViewAllPresetDrafts;

  // If a user has permission to edit a table, then they can view it
  const canViewTaxonomy = hasPermission(Permission.ViewTaxonomy) || canEditTaxonomy;
  const canViewTaxonomyTags = hasPermission(Permission.ViewTaxonomyTags) || canEditTaxonomy;
  const canViewCellRules = hasPermission(Permission.ViewStudyCellRules) || canEditCellRules;

  const { queryableTypes, isLoadingQueryableTypes } = useQueryableTypes();

  const taxonomy = useTaxonomy();

  const cellColorMappingFieldsContext = useCellColorMappingFieldsContext();

  type SettingsTab = 'biopsy' | 'cellRules' | 'labeling' | 'visualization' | 'taxonomy' | 'types' | 'presets';

  const TAB_DESCRIPTIONS: Record<SettingsTab, string> = {
    biopsy: 'Configure biopsy stain types available for slide classification across studies.',
    cellRules: 'Define global cell classification rules that determine how detected cells are categorized.',
    labeling: 'Manage slide tags and external labels applied during case intake and review.',
    visualization: 'Define how cell classes are color-coded in the viewer based on stain type.',
    taxonomy: 'Organize tissue and cell classification hierarchies and their associated tags.',
    types: 'Configure area annotation types, cluster types, and algorithm model definitions.',
    presets: 'Create, edit, publish, and control study visibility for calculate-features presets.',
  };

  const sectionTitleSx = {
    fontSize: {
      xs: '1.4rem',
      md: '1.7rem',
    },
    fontWeight: 600,
  };

  const tabs = useMemo(() => {
    const available: { value: SettingsTab; label: string; icon: React.ReactElement }[] = [];
    if (canEditStainTypes) {
      available.push({ value: 'biopsy', label: 'Biopsy & Staining', icon: <BiotechRoundedIcon /> });
    }
    if (canViewCellRules) {
      available.push({ value: 'cellRules', label: 'Cell Rules', icon: <CellRulesIcon /> });
    }
    if (canEditSlideTags || canEditExternalLabels) {
      available.push({ value: 'labeling', label: 'Labels & Tags', icon: <LabelRoundedIcon /> });
    }
    if (canEditCellColorMappings) {
      available.push({ value: 'visualization', label: 'Visualization', icon: <PaletteRoundedIcon /> });
    }
    if (canViewTaxonomy || canViewTaxonomyTags) {
      available.push({ value: 'taxonomy', label: 'Taxonomy', icon: <AccountTreeRoundedIcon /> });
    }
    if (canEditAreaTypes || canEditClusterTypes || canEditModelTypes) {
      available.push({ value: 'types', label: 'Types & Models', icon: <CategoryRoundedIcon /> });
    }
    if (canViewPresetManagement) {
      available.push({ value: 'presets', label: 'Presets', icon: <TuneRoundedIcon /> });
    }
    return available;
  }, [
    canEditStainTypes,
    canViewCellRules,
    canEditCellColorMappings,
    canEditSlideTags,
    canEditExternalLabels,
    canViewTaxonomy,
    canViewTaxonomyTags,
    canEditAreaTypes,
    canEditClusterTypes,
    canEditModelTypes,
    canViewPresetManagement,
  ]);

  const [activeTabParam, setActiveTabParam] = useQueryParamAsLocalState<SettingsTab>(
    'operationsSettingsTab',
    withDefault(StringParam, tabs[0]?.value ?? 'biopsy') as QueryParamConfig<SettingsTab | null | undefined>
  );
  const activeTab = tabs.some((tab) => tab.value === activeTabParam) ? activeTabParam : tabs[0]?.value ?? 'biopsy';
  const [isAuthoringPreset, setIsAuthoringPreset] = useState(false);
  const showGlobalSettingsChrome = !(activeTab === 'presets' && isAuthoringPreset);

  return (
    <Box sx={{ py: showGlobalSettingsChrome ? 3 : 0 }}>
      {showGlobalSettingsChrome && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton onClick={navigateBackFromAdmin} size="small">
              <ArrowBackRoundedIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={600}>
              Global Settings
            </Typography>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTabParam(value)} variant="scrollable">
              {tabs.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} icon={tab.icon} iconPosition="start" />
              ))}
            </Tabs>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {TAB_DESCRIPTIONS[activeTab]}
          </Typography>
        </>
      )}

      {activeTab === 'biopsy' && (
        <Grid container direction="column" spacing={3} alignItems="flex-start">
          {canEditStainTypes && (
            <Grid item container direction="column">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Stain Types
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Define the biopsy stain types available for slide classification across studies.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <RowSelectionContextProvider>
                  <TableEditingContextProvider idGetter={getStainTypeId}>
                    <StainTypesDataGrid />
                  </TableEditingContextProvider>
                </RowSelectionContextProvider>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      {activeTab === 'cellRules' && (
        <Grid container direction="column" spacing={3} alignItems="flex-start">
          {canViewCellRules && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Cell Rules
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Configure global cell classification rules that determine how detected cells are categorized.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <GlobalCellRulesPanel editable={canEditCellRules} />
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      {activeTab === 'labeling' && (
        <Grid container direction="column" spacing={3} alignItems="flex-start">
          {canEditSlideTags && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Slide Tags
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Define tags that can be applied to individual slides for filtering and organization.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <RowSelectionContextProvider>
                  <TableEditingContextProvider>
                    <SlideTagsDataGrid />
                  </TableEditingContextProvider>
                </RowSelectionContextProvider>
              </Grid>
            </Grid>
          )}
          {canEditExternalLabels && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  External Labels
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Manage external label definitions used for case intake and third-party integrations.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <RowSelectionContextProvider>
                  <TableEditingContextProvider>
                    <ExternalLabelsDataGrid />
                  </TableEditingContextProvider>
                </RowSelectionContextProvider>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      {activeTab === 'visualization' && (
        <Grid container direction="column" spacing={3} alignItems="flex-start">
          {canEditCellColorMappings && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Cell Color Mappings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Map cell classes to display colors in the slide viewer. Colors are resolved by stain type with a
                  fallback hierarchy: specific stain &rarr; IHC &rarr; no stain specified.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <RowSelectionContextProvider>
                  <TableEditingContextProvider fieldsContext={cellColorMappingFieldsContext}>
                    <CellColorMappingsDataGrid />
                  </TableEditingContextProvider>
                </RowSelectionContextProvider>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      {activeTab === 'taxonomy' && (
        <Grid container direction="column" spacing={3} alignItems="flex-start">
          {canViewTaxonomy && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  {canEditTaxonomy ? 'Taxonomy Editor' : 'Taxonomies'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {canEditTaxonomy
                    ? 'Build and edit tissue and cell classification hierarchies used for analysis.'
                    : 'View tissue and cell classification hierarchies used for analysis.'}
                </Typography>
              </Grid>
              <Grid item width="100%">
                <TaxonomyEditor />
              </Grid>
            </Grid>
          )}
          {canViewTaxonomyTags && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  {canEditTaxonomy ? 'Taxonomy Tag Management' : 'Taxonomy Tags'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Assign and organize tags within taxonomy nodes for granular classification.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <RowSelectionContextProvider>
                  <TableEditingContextProvider
                    fieldsContext={{
                      taxonomies: taxonomy.data,
                    }}
                  >
                    <TaxonomyTagsDataGrid />
                  </TableEditingContextProvider>
                </RowSelectionContextProvider>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      {activeTab === 'types' && (
        <Grid container direction="column" spacing={3} alignItems="flex-start">
          {canEditAreaTypes && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Area Types
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Define annotation area types available for region-of-interest marking in the viewer.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <RowSelectionContextProvider>
                  <TableEditingContextProvider
                    idGetter={getAreaTypeId}
                    fieldsContext={{ sourceTypes: queryableTypes.modelType }}
                  >
                    <AreaTypesDataGrid isLoading={isLoadingQueryableTypes} />
                  </TableEditingContextProvider>
                </RowSelectionContextProvider>
              </Grid>
            </Grid>
          )}
          {canEditClusterTypes && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Cluster Types
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Configure spatial cluster type definitions used in clustering analysis.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <RowSelectionContextProvider>
                  <TableEditingContextProvider>
                    <ClusterTypesDataGrid />
                  </TableEditingContextProvider>
                </RowSelectionContextProvider>
              </Grid>
            </Grid>
          )}
          {canEditModelTypes && (
            <Grid item container direction="column" width="100%">
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Model Types
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Register and manage algorithm model type definitions and their training configurations.
                </Typography>
              </Grid>
              <Grid item width="100%">
                <RowSelectionContextProvider>
                  <TableEditingContextProvider
                    idGetter={getModelTypeId}
                    fieldsContext={{
                      trainingTypes: queryableTypes.trainingType,
                    }}
                  >
                    <ModelTypesDataGrid />
                  </TableEditingContextProvider>
                </RowSelectionContextProvider>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
      {activeTab === 'presets' && canViewPresetManagement && (
        <Grid container direction="column" spacing={3} alignItems="flex-start">
          <Grid item container direction="column" width="100%">
            {showGlobalSettingsChrome && (
              <Grid item my={4}>
                <Typography variant="h5" sx={sectionTitleSx}>
                  Calculate-features presets
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Author presets with the shared post-processing and feature editor, or stage lifecycle changes and save
                  them together.
                </Typography>
              </Grid>
            )}
            <Grid item width="100%">
              <PresetsDataGrid onAuthoringChange={setIsAuthoringPreset} />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
});

export default OperationsSettings;
